<script setup lang="ts">
  import { useCreateEvChargerQuote } from "@/api/quotes/quotes"
  import { PERSON_TITLES as personTitles } from "@/constants/personTitles"
  import { Route } from "@/constants/route"
  import { useEvChargerQuote as defaultUseEvChargerQuote } from "@/lib/useEvChargerQuote"
  import { getPublicWebsiteUrl } from "@/lib/websiteUrls"
  import ErrorReporter from "@/services/ErrorReporter"
  import { toTypedSchema } from "@vee-validate/zod"
  import { useForm } from "vee-validate"
  import { inject, ref } from "vue"
  import { useRouter as defaultUseRouter } from "vue-router"
  import { AlertDescription } from "@/components/ui/alert"
  import Alert from "@/components/ui/alert/Alert.vue"
  import Button from "@/components/ui/button/Button.vue"
  import Checkbox from "@/components/ui/inputs/checkbox/Checkbox.vue"
  import TextInput from "@/components/ui/inputs/text-input/TextInput.vue"
  import { Link } from "@/components/ui/link"
  import { Select } from "@/components/ui/select"
  import { StepWizardProgressCard } from "@/components/ui/step-wizard"

  import { personalDetailsFormSchema } from "./forms/evChargerQuoteFormSchemas"

  const PUBLIC_WEBSITE_URL = getPublicWebsiteUrl()

  const useEvChargerQuote = inject(
    "useEvChargerQuote",
    defaultUseEvChargerQuote,
  )
  const useRouter = inject("useRouter", defaultUseRouter)

  const {
    evChargerQuotePersonalDetails,
    updatePersonalDetails,
    createEvChargerQuoteRequest,
  } = useEvChargerQuote()
  const consentToPrivacyPolicy = ref(false)

  const formSchema = toTypedSchema(personalDetailsFormSchema)

  const createQuote = useCreateEvChargerQuote()
  const router = useRouter()

  const { handleSubmit } = useForm({
    validationSchema: formSchema,
  })

  const onSubmit = handleSubmit(async (data) => {
    updatePersonalDetails(data)
    try {
      await createQuote.mutateAsync({
        data: createEvChargerQuoteRequest.value,
      })
    } catch (error) {
      ErrorReporter.report(error)
      return
    }

    if (!createQuote.error.value) {
      router.push(Route.evChargerQuoteConfirmation)
    }
  })
</script>

<template>
  <form novalidate @submit.prevent="onSubmit">
    <StepWizardProgressCard title="Personal details">
      <p class="mb-4 sm:mb-6">
        Please enter your details for us to send your full quote.
      </p>
      <div class="mb-3 space-y-6 sm:mb-4 sm:space-y-4">
        <div class="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
          <Select
            name="title"
            placeholder="Title"
            :model-value="evChargerQuotePersonalDetails.title"
            label="Title"
            :items="[...personTitles]"
          />
        </div>
        <div class="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2 sm:gap-y-4">
          <TextInput
            v-model="evChargerQuotePersonalDetails.firstName"
            name="firstName"
            type="text"
            label="First name"
          />
          <TextInput
            v-model="evChargerQuotePersonalDetails.lastName"
            name="lastName"
            type="text"
            label="Last name"
          />
          <TextInput
            v-model="evChargerQuotePersonalDetails.emailAddress"
            name="emailAddress"
            type="email"
            autocomplete="email"
            label="Email"
          />
          <TextInput
            v-model="evChargerQuotePersonalDetails.phoneNumber"
            name="phoneNumber"
            type="tel"
            autocomplete="tel"
            label="Phone"
          />
        </div>
        <div class="space-y-4">
          <Checkbox
            v-model:checked="evChargerQuotePersonalDetails.isAuthorisedToAlter"
            name="isAuthorisedToAlter"
            label="I confirm that I am the property owner or have obtained the necessary permission to carry out alterations."
          />
          <Checkbox
            v-model:checked="consentToPrivacyPolicy"
            name="consentToPrivacyPolicy"
            label="_"
          >
            <template #label>
              I agree to So Energy’s
              <a
                :href="`${PUBLIC_WEBSITE_URL}terms`"
                class="whitespace-nowrap text-primary-600 underline"
                target="_blank"
                >Terms & Conditions
              </a>
              and
              <a
                :href="`${PUBLIC_WEBSITE_URL}privacy/`"
                class="whitespace-nowrap text-primary-600 underline"
                target="_blank"
                >Privacy Policy
              </a>
            </template>
          </Checkbox>
          <Alert
            v-if="createQuote.error.value"
            variant="error"
            class="body-text mt-6 sm:mt-4"
          >
            <AlertDescription>
              We're having trouble processing your request. Please try again
              later. If the issue continues, please call our team at
              <Link class="link text-primary-600" to="tel:020 4625 1900"
                >020 4625 1900</Link
              >
              or email
              <Link class="link text-primary-600" to="mailto:ev@so.energy"
                >ev@so.energy</Link
              >
            </AlertDescription>
          </Alert>
        </div>
      </div>
      <template #next_button>
        <Button variant="tonal" size="md" :loading="createQuote.isPending.value"
          >Next
        </Button>
      </template>
    </StepWizardProgressCard>
  </form>
</template>
